import React, { useMemo } from "react";
import { Card, CardContent } from "../ui/card";
import { Users, Target, TrendingUp } from "lucide-react";
import { PpcFarmCohortLedgerData } from "../../types/ppcFarmTypes";
import { Skeleton } from "../ui/skeleton";

interface PpcFarmCohortMetricsBarProps {
  data: PpcFarmCohortLedgerData[];
  loading: boolean;
  error?: string | null;
}

const PpcFarmCohortMetricsBar: React.FC<PpcFarmCohortMetricsBarProps> = ({
  data,
  loading,
  error,
}) => {
  // Calculate metrics from cohort data
  const metrics = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        totalCohorts: 0,
        avgSellersPerCohort: 0,
        overallConversionRate: 0,
      };
    }

    // Total unique cohorts
    const totalCohorts = data.length;

    // Average sellers per cohort (using count field)
    const totalSellers = data.reduce(
      (sum, cohort) => sum + (cohort.count || 0),
      0,
    );
    const avgSellersPerCohort =
      totalCohorts > 0 ? Math.round(totalSellers / totalCohorts) : 0;

    // Overall conversion rate calculation: (sum of signedClients) / (sum of count) * 100
    let totalCount = 0;
    let totalSignedClients = 0;

    data.forEach((cohort) => {
      totalCount += cohort.count || 0;
      totalSignedClients += cohort.signedClients || 0;
    });

    const overallConversionRate =
      totalCount > 0 ? (totalSignedClients / totalCount) * 100 : 0;

    return {
      totalCohorts,
      avgSellersPerCohort,
      overallConversionRate,
    };
  }, [data]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {[...Array(3)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex flex-col items-center text-center space-y-2">
                <div className="flex items-center space-x-2">
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <Skeleton className="h-6 w-16" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="mb-6 border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
        <CardContent className="p-4">
          <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
            <TrendingUp className="h-4 w-4" />
            <span className="text-sm font-medium">
              Error loading metrics: {error}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const metricsData = [
    {
      label: "Total Cohorts",
      value: metrics.totalCohorts.toLocaleString(),
      icon: Users,
      color: "text-blue-600 dark:text-blue-400",
      bgColor: "bg-blue-100 dark:bg-blue-900/20",
    },
    {
      label: "Avg. Sellers per Cohort",
      value: metrics.avgSellersPerCohort.toLocaleString(),
      icon: Target,
      color: "text-green-600 dark:text-green-400",
      bgColor: "bg-green-100 dark:bg-green-900/20",
    },
    {
      label: "Overall Conversion Rate",
      value: `${metrics.overallConversionRate.toFixed(2)}%`,
      icon: TrendingUp,
      color: "text-purple-600 dark:text-purple-400",
      bgColor: "bg-purple-100 dark:bg-purple-900/20",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      {metricsData.map((metric, index) => {
        const IconComponent = metric.icon;
        return (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <CardContent className="p-3 flex items-center justify-center">
              <div className="text-center">
                <div className="flex items-center justify-center mb-1">
                  <IconComponent className={`h-4 w-4 ${metric.color} mr-2`} />
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    {metric.label}
                  </p>
                </div>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {metric.value}
                </p>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default PpcFarmCohortMetricsBar;
